/**
 * User preferences — active platform defaults (read-only today).
 */
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PaletteIcon from "@mui/icons-material/Palette";
import StraightenIcon from "@mui/icons-material/Straighten";
import type { ReactNode } from "react";

import { GlassSurface } from "../../layouts/components/GlassSurface";

interface PreferenceRow {
	title: string;
	description: string;
	current: string;
	icon: ReactNode;
}

const PREFERENCES: PreferenceRow[] = [
	{
		title: "Units & Locale",
		description: "Measurement units, language, date and number formats.",
		current: "Metric (SI) · English",
		icon: <LanguageIcon />,
	},
	{
		title: "Measurement Conventions",
		description: "Default sample depths and salinity reporting basis.",
		current: "0–30 cm · EC reported as dS/m",
		icon: <StraightenIcon />,
	},
	{
		title: "Notifications",
		description: "Email digests for completed reports and alert thresholds.",
		current: "In-app only",
		icon: <NotificationsIcon />,
	},
	{
		title: "Appearance",
		description: "Workspace chrome and print-friendly report styling.",
		current: "Dark soil-intelligence workspace · Light print/PDF reports",
		icon: <PaletteIcon />,
	},
];

export function PreferencesPanel() {
	return (
		<Box data-testid="account-preferences-panel">
			<Typography
				variant="body2"
				sx={{ mb: 3, color: "workspace.textSecondary", maxWidth: 720 }}
			>
				FlahaSOIL applies the sensible defaults below across your
				soil-science workflows. These preferences are read-only today and
				will become editable in a future release.
			</Typography>
			<Box
				sx={{
					display: "grid",
					gap: 2,
					gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
				}}
			>
				{PREFERENCES.map((p) => (
					<GlassSurface key={p.title} intensity="standard" depth="raised" sx={{ p: 2.25 }}>
						<Stack
							direction="row"
							spacing={1.5}
							alignItems="center"
							sx={{ mb: 1 }}
						>
							<Box sx={{ color: "workspace.green", display: "flex" }} aria-hidden>
								{p.icon}
							</Box>
							<Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
								{p.title}
							</Typography>
							<Chip
								label="Editable soon"
								size="small"
								variant="outlined"
								sx={{
									borderColor: "workspace.borderStrong",
									color: "workspace.textMuted",
								}}
							/>
						</Stack>
						<Typography
							variant="body2"
							sx={{ mb: 1, color: "workspace.textSecondary" }}
						>
							{p.description}
						</Typography>
						<Divider sx={{ mb: 1, borderColor: "workspace.border" }} />
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
							gap={1}
						>
							<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
								Current default
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
								{p.current}
							</Typography>
						</Stack>
					</GlassSurface>
				))}
			</Box>
		</Box>
	);
}
